import { ArrowRight, CalendarClock, Download, Rocket, Share2, Sparkles, Trash2, Users } from "lucide-react";
import { formatDate } from "@/lib/portalHelpers";


const statCardTone = [
  "border-[#D4AF37]/25 bg-[#fff8e8]",
  "border-[#d3def6] bg-[#f5f8ff]",
  "border-[#ead8ff] bg-[#faf5ff]",
  "border-emerald-200 bg-emerald-50",
  "border-[#d7e7df] bg-[#f4fbf7]",
];

const eventLabel = (value) => String(value || "signal").replace(/_/g, " ");

const formatTimestamp = (value) => {
  if (!value) return "Just now";
  try {
    return new Intl.DateTimeFormat("en-SG", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return formatDate(value);
  }
};

const AnalyticsStatCard = ({ index, label, value, detail, testId }) => (
  <div className={`rounded-[26px] border p-5 ${statCardTone[index % statCardTone.length]}`} data-testid={testId}>
    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8b6d16]" data-testid={`${testId}-label`}>{label}</p>
    <p className="mt-3 text-3xl font-semibold text-[#001935]" data-testid={`${testId}-value`}>{value}</p>
    <p className="mt-2 text-sm leading-6 text-[#42566f]" data-testid={`${testId}-detail`}>{detail}</p>
  </div>
);

const FunnelStepRow = ({ item, maxCount, previousCount, testId }) => {
  const width = Math.max(10, Math.round(((item.count || 0) / maxCount) * 100));
  const conversionRate = previousCount ? Math.round(((item.count || 0) / previousCount) * 100) : 100;

  return (
    <div className="rounded-[24px] border border-[#001935]/10 bg-[#fcfaf5] p-4" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]" data-testid={`${testId}-label`}>{item.label}</p>
          <p className="mt-1 text-sm text-[#42566f]" data-testid={`${testId}-meta`}>
            {previousCount ? `${conversionRate}% of previous stage` : "Entry point to the operator funnel"}
          </p>
        </div>
        <p className="text-2xl font-semibold text-[#001935]" data-testid={`${testId}-count`}>{item.count || 0}</p>
      </div>
      <div className="mt-4 h-3 rounded-full bg-[#ebe4d5]" data-testid={`${testId}-track`}>
        <div className="h-3 rounded-full bg-gradient-to-r from-[#D4AF37] via-[#c89a18] to-[#001935]" data-testid={`${testId}-fill`} style={{ width: `${width}%` }} />
      </div>
    </div>
  );
};

const ShortlistListingRow = ({ item, maxCount, testId }) => {
  const width = Math.max(10, Math.round(((item.total_engagement || 0) / maxCount) * 100));

  return (
    <div className="rounded-[22px] border border-[#001935]/10 bg-white px-4 py-4" data-testid={testId}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-base font-semibold text-[#001935]" data-testid={`${testId}-title`}>{item.listing_title || "Shortlisted Listing"}</p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs font-bold uppercase tracking-[0.16em] text-[#6b6252]">
            <span data-testid={`${testId}-saves`}>Saves {item.save_count || 0}</span>
            <span data-testid={`${testId}-shares`}>Shares {item.share_count || 0}</span>
            <span data-testid={`${testId}-exports`}>Exports {item.export_count || 0}</span>
          </div>
        </div>
        <p className="text-lg font-semibold text-[#8b6d16]" data-testid={`${testId}-total`}>{item.total_engagement || 0}</p>
      </div>
      <div className="mt-4 h-2.5 rounded-full bg-[#f1ebdf]" data-testid={`${testId}-track`}>
        <div className="h-2.5 rounded-full bg-[#D4AF37]" data-testid={`${testId}-fill`} style={{ width: `${width}%` }} />
      </div>
    </div>
  );
};

const CompletionRow = ({ item, index }) => (
  <div className="rounded-[22px] border border-[#001935]/10 bg-white px-4 py-4" data-testid={`dashboard-analytics-completion-${index}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="text-base font-semibold text-[#001935]" data-testid={`dashboard-analytics-completion-${index}-name`}>{item.name || "Unnamed agent"}</p>
        <p className="mt-1 text-xs font-bold uppercase tracking-[0.16em] text-[#8b6d16]" data-testid={`dashboard-analytics-completion-${index}-slug`}>
          {item.subdomain_slug ? `/agent/${item.subdomain_slug}` : item.plan_type || "Agent portal"}
        </p>
      </div>
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#6b6252]" data-testid={`dashboard-analytics-completion-${index}-date`}>
        {formatDate(item.onboarding_completed_at)}
      </p>
    </div>
  </div>
);

const RecentEventRow = ({ item, index }) => (
  <div className="rounded-[22px] border border-[#001935]/10 bg-white px-4 py-4" data-testid={`dashboard-analytics-event-${index}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]" data-testid={`dashboard-analytics-event-${index}-type`}>{eventLabel(item.event_type)}</p>
        <p className="mt-2 text-sm leading-7 text-[#001935]" data-testid={`dashboard-analytics-event-${index}-summary`}>{item.summary || "Signal captured"}</p>
      </div>
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#6b6252]" data-testid={`dashboard-analytics-event-${index}-time`}>
        {formatTimestamp(item.created_at)}
      </p>
    </div>
  </div>
);

export const AdminAnalyticsExpansionPanel = ({ analytics, cleanupBusy, cleanupResult, isAdmin, onRunCleanup }) => {
  if (!analytics) return null;

  const overview = analytics.overview || {};
  const engagementFunnel = analytics.engagement_funnel || [];
  const shortlist = analytics.shortlist || {};
  const onboarding = analytics.onboarding || {};
  const recentEvents = analytics.recent_events || [];
  const topListings = shortlist.top_listings || [];
  const recentCompletions = onboarding.recent_completions || [];
  const maxFunnelCount = Math.max(...engagementFunnel.map((item) => item.count || 0), 1);
  const maxShortlistCount = Math.max(...topListings.map((item) => item.total_engagement || 0), 1);
  const onboardingProgressWidth = Math.min(100, Math.max(8, Number(onboarding.completion_rate || 0)));
  const cleanupTone = cleanupResult?.tone === "error"
    ? "border-red-200 bg-red-50 text-red-700"
    : "border-emerald-200 bg-emerald-50 text-emerald-800";

  return (
    <section className="space-y-6" data-testid="dashboard-analytics-expansion-panel">
      <div className="rounded-[32px] border border-[#D4AF37]/30 bg-[#fffaf1] p-6 shadow-[0_22px_70px_rgba(0,25,53,0.08)] md:p-8" data-testid="dashboard-analytics-header-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#8b6d16]" data-testid="dashboard-analytics-eyebrow">Batch F · Operator Analytics</p>
            <h2 className="mt-3 text-3xl font-semibold text-[#001935]" data-testid="dashboard-analytics-heading">30-day funnel view for lead quality, shortlist intent, and onboarding momentum</h2>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-[#42566f]" data-testid="dashboard-analytics-subcopy">
              Track how discovery signals turn into shortlist intent, advisor demand, private viewings, and fully launched agent portals without leaving the admin summary screen.
            </p>
          </div>
          <div className="rounded-[24px] border border-[#D4AF37]/25 bg-white px-4 py-4 text-right" data-testid="dashboard-analytics-tracked-events-pill">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Tracked events</p>
            <p className="mt-2 text-3xl font-semibold text-[#001935]" data-testid="dashboard-analytics-tracked-events-value">{overview.tracked_events_30d || 0}</p>
            <p className="mt-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#6b6252]">Last 30 days</p>
          </div>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <AnalyticsStatCard detail="New enquiries entering the pipeline." index={0} label="Lead captures" testId="dashboard-analytics-overview-lead-captures" value={overview.lead_captures_30d || 0} />
          <AnalyticsStatCard detail="Listings saved to shortlist across tracked sessions." index={1} label="Shortlist saves" testId="dashboard-analytics-overview-shortlist-saves" value={overview.shortlist_saves_30d || 0} />
          <AnalyticsStatCard detail="Shares sent out from shortlist studio links." index={2} label="Shortlist shares" testId="dashboard-analytics-overview-shortlist-shares" value={shortlist.shares_30d || 0} />
          <AnalyticsStatCard detail="Booked viewing requests generated from live intent." index={3} label="Viewing requests" testId="dashboard-analytics-overview-viewing-requests" value={overview.viewing_requests_30d || 0} />
          <AnalyticsStatCard detail="Agents that completed the onboarding wizard." index={4} label="Onboarding completions" testId="dashboard-analytics-overview-onboarding-completions" value={overview.onboarding_completions_30d || 0} />
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
        <div className="rounded-[30px] border border-[#001935]/10 bg-white/95 p-6 shadow-[0_20px_60px_rgba(0,25,53,0.08)]" data-testid="dashboard-analytics-funnel-card">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-[#fff4db] p-3 text-[#8b6d16]" data-testid="dashboard-analytics-funnel-icon"><Sparkles className="h-5 w-5" /></div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Engagement funnel</p>
              <h3 className="mt-2 text-2xl font-semibold text-[#001935]" data-testid="dashboard-analytics-funnel-heading">Intent progression from first enquiry to viewing request</h3>
            </div>
          </div>
          <div className="mt-6 grid gap-4" data-testid="dashboard-analytics-funnel-list">
            {engagementFunnel.length ? engagementFunnel.map((item, index) => (
              <FunnelStepRow item={item} key={item.key} maxCount={maxFunnelCount} previousCount={index > 0 ? engagementFunnel[index - 1]?.count || 0 : 0} testId={`dashboard-analytics-funnel-step-${item.key}`} />
            )) : (
              <div className="rounded-[24px] border border-[#001935]/10 bg-[#fcfaf5] px-4 py-5 text-sm text-[#42566f]" data-testid="dashboard-analytics-funnel-empty-state">
                Funnel data will appear here as new lead, shortlist, advisor, and viewing signals are captured.
              </div>
            )}
          </div>
        </div>

        <div className="grid gap-6">
          <div className="rounded-[30px] border border-[#001935]/10 bg-white/95 p-6 shadow-[0_20px_60px_rgba(0,25,53,0.08)]" data-testid="dashboard-analytics-shortlist-card">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-[#f6efff] p-3 text-[#6b4fa6]" data-testid="dashboard-analytics-shortlist-icon"><Share2 className="h-5 w-5" /></div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Shortlist engagement</p>
                <h3 className="mt-2 text-2xl font-semibold text-[#001935]" data-testid="dashboard-analytics-shortlist-heading">Top listings earning save, share, and export intent</h3>
              </div>
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-3">
              <div className="rounded-[22px] border border-[#001935]/10 bg-[#fcfaf5] px-4 py-4" data-testid="dashboard-analytics-shortlist-saves-card">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Saves</p>
                <p className="mt-2 text-2xl font-semibold text-[#001935]">{shortlist.saves_30d || 0}</p>
              </div>
              <div className="rounded-[22px] border border-[#001935]/10 bg-[#fcfaf5] px-4 py-4" data-testid="dashboard-analytics-shortlist-shares-card">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Shares</p>
                <p className="mt-2 text-2xl font-semibold text-[#001935]">{shortlist.shares_30d || 0}</p>
              </div>
              <div className="rounded-[22px] border border-[#001935]/10 bg-[#fcfaf5] px-4 py-4" data-testid="dashboard-analytics-shortlist-exports-card">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Exports</p>
                <p className="mt-2 flex items-center gap-2 text-2xl font-semibold text-[#001935]"><Download className="h-4 w-4 text-[#8b6d16]" />{shortlist.exports_30d || 0}</p>
              </div>
            </div>

            <div className="mt-5 grid gap-3" data-testid="dashboard-analytics-shortlist-top-list">
              {topListings.length ? topListings.map((item) => (
                <ShortlistListingRow item={item} key={item.listing_id || item.listing_title} maxCount={maxShortlistCount} testId={`dashboard-analytics-shortlist-row-${item.listing_id || item.listing_title?.replace(/[^a-z0-9]+/gi, "-").toLowerCase() || "listing"}`} />
              )) : (
                <div className="rounded-[22px] border border-[#001935]/10 bg-[#fcfaf5] px-4 py-5 text-sm text-[#42566f]" data-testid="dashboard-analytics-shortlist-empty-state">
                  No shortlist activity is available yet. Save, share, or export actions will populate this leaderboard automatically.
                </div>
              )}
            </div>
          </div>

          <div className="rounded-[30px] border border-[#001935]/10 bg-white/95 p-6 shadow-[0_20px_60px_rgba(0,25,53,0.08)]" data-testid="dashboard-analytics-onboarding-card">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-[#eefaf5] p-3 text-emerald-700" data-testid="dashboard-analytics-onboarding-icon"><Rocket className="h-5 w-5" /></div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Onboarding momentum</p>
                <h3 className="mt-2 text-2xl font-semibold text-[#001935]" data-testid="dashboard-analytics-onboarding-heading">Portal launch readiness across active agents</h3>
              </div>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-3">
              <div className="rounded-[22px] border border-[#001935]/10 bg-[#fcfaf5] px-4 py-4" data-testid="dashboard-analytics-onboarding-total-agents-card">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Tracked agents</p>
                <p className="mt-2 text-2xl font-semibold text-[#001935]">{onboarding.total_agents || 0}</p>
              </div>
              <div className="rounded-[22px] border border-[#001935]/10 bg-[#fcfaf5] px-4 py-4" data-testid="dashboard-analytics-onboarding-completed-card">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Completed</p>
                <p className="mt-2 text-2xl font-semibold text-[#001935]">{onboarding.completed_count || 0}</p>
              </div>
              <div className="rounded-[22px] border border-[#001935]/10 bg-[#fcfaf5] px-4 py-4" data-testid="dashboard-analytics-onboarding-pending-card">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Pending</p>
                <p className="mt-2 text-2xl font-semibold text-[#001935]">{onboarding.pending_count || 0}</p>
              </div>
            </div>

            <div className="mt-5 rounded-[24px] border border-[#001935]/10 bg-[#fcfaf5] p-4" data-testid="dashboard-analytics-onboarding-readiness-panel">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Completion rate</p>
                  <p className="mt-1 text-sm text-[#42566f]">Average readiness score: <span className="font-semibold text-[#001935]" data-testid="dashboard-analytics-onboarding-readiness-score">{onboarding.avg_readiness_score || 0}%</span></p>
                </div>
                <p className="text-2xl font-semibold text-[#001935]" data-testid="dashboard-analytics-onboarding-completion-rate">{onboarding.completion_rate || 0}%</p>
              </div>
              <div className="mt-4 h-3 rounded-full bg-[#ebe4d5]" data-testid="dashboard-analytics-onboarding-progress-track">
                <div className="h-3 rounded-full bg-gradient-to-r from-emerald-500 via-[#D4AF37] to-[#001935]" data-testid="dashboard-analytics-onboarding-progress-fill" style={{ width: `${onboardingProgressWidth}%` }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[0.92fr_1.08fr]">
        <div className="rounded-[30px] border border-[#001935]/10 bg-white/95 p-6 shadow-[0_20px_60px_rgba(0,25,53,0.08)]" data-testid="dashboard-analytics-recent-completions-card">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-[#eef4ff] p-3 text-[#35548f]" data-testid="dashboard-analytics-recent-completions-icon"><Users className="h-5 w-5" /></div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Recent launch completions</p>
              <h3 className="mt-2 text-2xl font-semibold text-[#001935]" data-testid="dashboard-analytics-recent-completions-heading">Latest agents to finish the onboarding wizard</h3>
            </div>
          </div>
          <div className="mt-6 grid gap-3" data-testid="dashboard-analytics-recent-completions-list">
            {recentCompletions.length ? recentCompletions.map((item, index) => <CompletionRow index={index} item={item} key={item.id || `${item.name}-${index}`} />) : (
              <div className="rounded-[22px] border border-[#001935]/10 bg-[#fcfaf5] px-4 py-5 text-sm text-[#42566f]" data-testid="dashboard-analytics-recent-completions-empty-state">
                Completion milestones will appear here after agents finish the onboarding wizard.
              </div>
            )}
          </div>

          {isAdmin ? (
            <div className="mt-6 rounded-[24px] border border-[#D4AF37]/25 bg-[#fffaf1] p-5" data-testid="dashboard-analytics-cleanup-card">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Go-live hygiene</p>
                  <h4 className="mt-2 text-xl font-semibold text-[#001935]" data-testid="dashboard-analytics-cleanup-heading">One-click admin cleanup utility</h4>
                  <p className="mt-3 max-w-2xl text-sm leading-7 text-[#42566f]" data-testid="dashboard-analytics-cleanup-detail">
                    Removes clearly tagged rehearsal or test leads, lead-intent events, and private transaction uploads only. Live users, subscriptions, listings, and real customer records stay untouched.
                  </p>
                </div>
                <button className="gold-button" data-testid="dashboard-analytics-cleanup-button" disabled={cleanupBusy} onClick={onRunCleanup} type="button">
                  <Trash2 className="mr-2 h-4 w-4" />
                  {cleanupBusy ? "Running Cleanup..." : "Run Cleanup Utility"}
                </button>
              </div>
              {cleanupResult?.message ? (
                <div className={`mt-5 rounded-[20px] border px-4 py-4 text-sm leading-7 ${cleanupTone}`} data-testid="dashboard-analytics-cleanup-result">
                  <p className="font-semibold" data-testid="dashboard-analytics-cleanup-result-message">{cleanupResult.message}</p>
                  {cleanupResult.tone !== "error" ? (
                    <div className="mt-3 flex flex-wrap gap-4 text-xs font-bold uppercase tracking-[0.16em]">
                      <span data-testid="dashboard-analytics-cleanup-result-leads">Leads {cleanupResult.leads_removed || 0}</span>
                      <span data-testid="dashboard-analytics-cleanup-result-events">Events {cleanupResult.lead_intent_events_removed || 0}</span>
                      <span data-testid="dashboard-analytics-cleanup-result-transactions">Private rows {cleanupResult.private_transactions_removed || 0}</span>
                      <span data-testid="dashboard-analytics-cleanup-result-batches">Batches {cleanupResult.private_transaction_batches_removed || 0}</span>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="rounded-[30px] border border-[#001935]/10 bg-white/95 p-6 shadow-[0_20px_60px_rgba(0,25,53,0.08)]" data-testid="dashboard-analytics-recent-events-card">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-[#fff4db] p-3 text-[#8b6d16]" data-testid="dashboard-analytics-recent-events-icon"><CalendarClock className="h-5 w-5" /></div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Live activity feed</p>
              <h3 className="mt-2 text-2xl font-semibold text-[#001935]" data-testid="dashboard-analytics-recent-events-heading">Newest tracked actions across leads, shortlists, and onboarding</h3>
            </div>
          </div>
          <div className="mt-6 grid gap-3" data-testid="dashboard-analytics-recent-events-list">
            {recentEvents.length ? recentEvents.map((item, index) => <RecentEventRow index={index} item={item} key={item.id || `${item.event_type}-${index}`} />) : (
              <div className="rounded-[22px] border border-[#001935]/10 bg-[#fcfaf5] px-4 py-5 text-sm text-[#42566f]" data-testid="dashboard-analytics-recent-events-empty-state">
                Once new lead, shortlist, and onboarding activity is tracked, the latest signals will stream into this timeline.
              </div>
            )}
          </div>
          <div className="mt-6 rounded-[22px] border border-[#001935]/10 bg-[#fcfaf5] px-4 py-4 text-sm leading-7 text-[#42566f]" data-testid="dashboard-analytics-recent-events-footer">
            <div className="flex items-center gap-2 text-[#001935]">
              <ArrowRight className="h-4 w-4 text-[#8b6d16]" />
              <span className="font-semibold" data-testid="dashboard-analytics-recent-events-footer-label">Operator takeaway</span>
            </div>
            <p className="mt-2" data-testid="dashboard-analytics-recent-events-footer-copy">
              Use this feed alongside Mission Control to spot whether shortlist energy is turning into viewings and whether newly activated agents are reaching launch readiness fast enough.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};